<script lang="ts" setup>
import storeX from '@/store';
import { useStore } from 'vuex';
import { set, useIntervalFn } from '@vueuse/core';
import { computed, watchEffect, onUnmounted } from 'vue';
import useGemMeter from '../composable/useGemMeter';
import { MutationTypes } from '@/modules/gem-meter/store/mutations';
import { BuildColorAndIcon } from '../utils';
import OnboardingStatus from './OnboardingStatus.vue';
import Loading from './Loading.vue';
import { init, listeners, removeListeners, runPageValidation } from '../GemMeterEngine/Engine/useGemMeterController';
import { isPagePublished } from '../GemMeterEngine/Engine/useGemMeterStore';
import emitter from '@/emitter';
import { CHECK_LIST, MODULE_NAME } from '@modules/gem-meter';
import { ActionTypes } from '../store';
import trackEvent, { GEM_METER_EVENT_ID } from '@/modules/common/utils/tracker';

let interval: any = null;
let preElementCount = 0;
const defaultWeakSEO = {
  id: CHECK_LIST.WEAK_SEO.id,
  key: CHECK_LIST.WEAK_SEO.key,
  screen: {
    lg: {
      errorElements: {},
      status: null,
      elementLength: 0,
    },
    md: {
      errorElements: {},
      status: null,
      elementLength: 0,
    },
    sm: {
      errorElements: {},
      status: null,
      elementLength: 0,
    },
    xs: {
      errorElements: {},
      status: null,
      elementLength: 0,
    },
  },
  isPageValidate: true,
};
const store = useStore();
const { degProgressValue, gemMeterOBD } = useGemMeter();

const deg = ref(0);
const bottomGemeterPannel = ref(90);
const isTrackingRunning = ref(false);

const loadAllMER = computed(() => store.state.editor.loadFinishALLMER);
const latestProductInShopifyStore = computed(() => store.state.app.latestProductInShopifyStore);
const isInitialize = computed(() => store.state.gemMeter.isInitialize);
const lastUsedAt = computed(() => store.state.app.shopInfo.lastGemMeterUsedAt);
const enablePassword = computed(() => store.state.app.enablePassword);

const currentDate = computed(() => {
  const now = new Date();

  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
});

const loading = computed(() => store.state.gemMeter.loading);
const openModal = computed(() => store.state.gemMeter.openModal);

const toggleModal = () => {
  // Prevent open modal when gem-meter is not finish initialize
  if (loading.value || !isInitialize.value) {
    return;
  }

  store.commit(`gemMeter/${MutationTypes.OPEN_MODAL_CHECKLIST}`, !openModal.value);
  if (isTrackingRunning.value) {
    return;
  }

  if (!lastUsedAt.value) {
    isTrackingRunning.value = true;
    store
      .dispatch(`gemMeter/${ActionTypes.UPDATE_LAST_USED}`, currentDate.value)
      .then(() => {
        trackEvent({
          eventId: GEM_METER_EVENT_ID.ACTIVE_USER,
          properties: {},
        });
      })
      .finally(() => {
        isTrackingRunning.value = false;
      });
  } else if (lastUsedAt.value && lastUsedAt.value !== currentDate.value) {
    isTrackingRunning.value = true;
    store
      .dispatch(`gemMeter/${ActionTypes.UPDATE_LAST_USED}`, currentDate.value)
      .then(() => {
        trackEvent({
          eventId: GEM_METER_EVENT_ID.RETENTION,
          properties: {},
        });
      })
      .finally(() => {
        isTrackingRunning.value = false;
      });
  }
};

const progressStyle = reactive({
  icon: '',
  color: '',
});

watchEffect(() => {
  if (!loadAllMER.value) return;
  const { color, statusIcon } = BuildColorAndIcon(degProgressValue.value);
  progressStyle.color = color;
  progressStyle.icon = statusIcon;

  const { pause } = useIntervalFn(() => {
    if (deg.value >= degProgressValue.value) {
      set(deg, degProgressValue.value);
      if (deg.value >= 0) {
        store.commit(`${MODULE_NAME}/${MutationTypes.SET_LOADING}`, false);
      }
      pause();
    } else {
      set(deg, deg.value + 2);
    }
  }, 10);
});

watchEffect(() => {
  if (loadAllMER.value && latestProductInShopifyStore.value) {
    if (!store.state.app?.pageInfo?.page?.gRecommendations) {
      if (!isInitialize.value) {
        init();
      }
    } else {
      const gRecommendationJson = JSON.parse(store.state.app?.pageInfo?.page?.gRecommendations);

      if (gRecommendationJson && gRecommendationJson.version === store.state.gemMeter.version && !isInitialize.value) {
        store.commit(`gemMeter/${MutationTypes.SET_IS_INITIALIZE}`, true);
        let newDataJson = { ...gRecommendationJson };
        const isPublished = isPagePublished();
        if (!isPublished) {
          newDataJson = {
            ...newDataJson,
            pageRules: newDataJson.pageRules.filter((item) => item.id !== CHECK_LIST.WEAK_SEO.id),
            convertableStandards: {
              ...newDataJson.convertableStandards,
              [CHECK_LIST.WEAK_SEO.id]: defaultWeakSEO,
            },
          };
        }
        store.commit(`${MODULE_NAME}/${MutationTypes.UPDATE_STORE}`, newDataJson);

        if (isPublished) {
          runPageValidation('', [CHECK_LIST.WEAK_SEO]);
        }
        listeners();
      } else {
        if (!isInitialize.value) {
          init();
        }
      }
    }
  }
});

onMounted(() => {
  const BOTTOM = 153;
  if (enablePassword.value) {
    bottomGemeterPannel.value = BOTTOM;
  }

  emitter.on('init:password', () => {
    bottomGemeterPannel.value = BOTTOM;
  });

  emitter.on('close:modal-password', () => {
    if (!enablePassword.value) {
      bottomGemeterPannel.value = 90;
    }
  });
});

emitter.on('editor:start-counter-mer', () => {
  store.commit(`${MODULE_NAME}/${MutationTypes.SET_LOADING}`, true);
});

watchEffect(() => {
  if (!loadAllMER.value) {
    if (interval) {
      clearInterval(interval);
    }
    interval = setInterval(() => {
      if (preElementCount === (window as any).__countElement || (window as any).__countElement === 0) {
        storeX.state.editor.loadFinishALLMER = true;
        clearInterval(interval);
        interval = null;
      }
      preElementCount = (window as any).__countElement;
    }, 2000);
  } else {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  }
});

onUnmounted(() => {
  removeListeners();
  preElementCount = 0;
  if (interval) {
    clearInterval(interval);
  }
});
</script>

<template>
  <div
    class="gp-meter-app duration-300"
    :style="{
      '--progress': `${deg}deg`,
      bottom: `${bottomGemeterPannel}px`,
      background: `conic-gradient(${progressStyle.color} var(--progress),#fff 0deg)`,
    }"
    :class="{
      open: openModal,
      '!cursor-progress': loading,
    }"
    @click="toggleModal">
    <div class="gp-meter-progress"></div>
    <template v-if="(!openModal || !gemMeterOBD) && !loading">
      <OnboardingStatus />
    </template>
    <template v-if="loading">
      <Loading class="z-50" />
    </template>
    <template v-if="openModal && gemMeterOBD">
      <div class="z-10 select-none" v-html="progressStyle.icon"></div>
    </template>
  </div>
</template>

<style lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';

.gp-meter {
  .gp-meter-app {
    position: fixed;
    bottom: 90px;
    right: 20px;
    background-color: $light3;
    width: 60px;
    height: 60px;
    z-index: 99999;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    filter: drop-shadow(0px -2px 8px rgba(0, 0, 0, 0.06)) drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.12));

    &.open {
      .icon {
        transform: rotate(-90deg);
      }
    }

    .icon {
      font-size: 24px;
      color: $black4;
      transform: rotate(90deg);
    }

    &.has-progress-yellow {
      background: conic-gradient($cYellow var(--progress), $light1 0deg);
    }

    &.has-progress-green {
      background: conic-gradient(#00c853 var(--progress), $light1 0deg);
    }

    &.has-progress-blue {
      background: conic-gradient(#3c67ff var(--progress), $light1 0deg);
    }

    .gp-meter-progress {
      background-color: #fff;
      width: 52px;
      height: 52px;
      position: absolute;
      border-radius: 50%;
      user-select: none;
    }
  }
}
</style>
